import React from 'react'
import { Button } from '../components/button'
import { X } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components'
import { cn } from '../lib'

/**
 * Props for SideClose component.
 * @property isDisabled - Whether the close action is disabled
 * @property onClose - Handler for the close action
 * @property variant - Button variant, defaults to 'iconDestructive'
 * @property size - Button size, defaults to default button size
 * @property tooltip - Optional tooltip text for the close button
 * @property className - Additional CSS classes
 * @property icon - Custom icon component, defaults to X from lucide-react
 */
export interface SideCloseProps {
  isDisabled?: boolean
  onClose: () => void
  variant?: 'iconDestructive' | 'icon' | 'secondary' | 'outline' | 'ghost'
  size?: 'sm' | 'lg' | 'icon-sm' | 'icon-md' | 'icon-lg'
  tooltip?: string
  className?: string
  icon?: React.ReactNode
}

/**
 * SideClose - A reusable close button component for side panels and sheets
 *
 * This component provides a standardized way to add close functionality to side panels,
 * sheets, and other overlay components. It includes optional tooltip support and
 * customizable styling.
 *
 * @example
 * ```tsx
 * <SideClose
 *   onClose={() => setIsOpen(false)}
 *   tooltip="Close panel"
 *   variant="iconDestructive"
 * />
 * ```
 */
export const SideClose: React.FC<SideCloseProps> = ({
  isDisabled = false,
  onClose,
  variant = 'iconDestructive',
  size,
  tooltip = 'Close',
  className = 'w-1/5',
  icon = <X className="h-4 w-4" />,
}) => {
  const button = (
    <Button
      variant={variant}
      size={size}
      onClick={onClose}
      disabled={isDisabled}
      className={cn(className)}
      data-testid="side-close-button"
    >
      {icon}
    </Button>
  )

  if (tooltip) {
    return (
      <div className="flex gap-2 items-center justify-center">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>{button}</TooltipTrigger>
            <TooltipContent>
              <p>{tooltip}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    )
  }

  return <div className="flex gap-2 items-center justify-center">{button}</div>
}

SideClose.displayName = 'SideClose'
